import { convertString2JSON, removeDuplicateInArray, StorageUtil, toCamel } from "utils";
import { getInteractor } from "./local.service";
import { AppConstant, KeyConstant, SystemConstant } from "const";
import { saveMessageInQueue } from "utils/message.utils";

/**
 *
 * @param {*} prefixKey
 * @param {*} messageIds
 * @returns min disappear_time of updated message list in local db
 *
 * update disappear_time to local db
 */
export const updateDisappearTime = async (prefixKey, messageIds) => {
  try {
    const localMessages = await getInteractor(prefixKey).LocalMessageService.find({
      id: messageIds,
    });

    if (Array.isArray(localMessages) && localMessages.length > 0) {
      const disappearTimeList = [];
      const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
      for (let index = 0; index < localMessages.length; index++) {
        const message = localMessages[index];

        const localSeen = await getInteractor(prefixKey).LocalSeenMemberService.findOne({
          source_id: message.source_id,
        });
        const messageOptions = message.options ? convertString2JSON(message.options, {}) : {};
        const { disappearing_f, time_to_live } = messageOptions;
        const isOtherMessage = message.sender_id !== accountId;
        if (
          Boolean(isOtherMessage) &&
          false === Boolean(message.disappear_time) &&
          Boolean(disappearing_f) &&
          Number(time_to_live) > 0
        ) {
          const disappearTime = Number(time_to_live) + (localSeen?.seen_at || Date.now());
          await getInteractor(prefixKey).LocalMessageService.update(
            { disappear_time: disappearTime },
            { id: message.id },
          );
          disappearTimeList.push(disappearTime);
        }
      }

      if (disappearTimeList.length > 0) return Math.min(...disappearTimeList);
    }
  } catch (error) {
    console.error("update message to read status error: ", error);
  }

  return 0;
};

export const restoreDeleteOnlyMe = async (prefixKey, restoreMessage) => {
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  restoreMessage = toCamel(restoreMessage);
  // Delete api call record with task = delayMessage
  await getInteractor(prefixKey).LocalApiCallService.deleteWithCondition({
    task: AppConstant.API_CALL_TASK.delayMessage,
  });

  // Update state for related message
  // Check disappearTime < Date.now() => skip update state = active
  let updateMessageListId = [];
  // Check parentMessage
  const localDeleteMessage = await getInteractor(prefixKey).LocalMessageService.findOne({ id: restoreMessage.id });
  if (
    localDeleteMessage &&
    (false === Boolean(localDeleteMessage.disappear_time) || localDeleteMessage.disappear_time > Date.now())
  ) {
    updateMessageListId.push(restoreMessage.id);
  }

  /**
   * Check thread messages
   * Restore if  message is not disappear and not delete only me
   */
  const localDeleteThreadMsgs = await getInteractor(prefixKey).LocalMessageService.find({
    thread_id: restoreMessage.sourceId,
  });

  const validRestoreThreadMessagesId = [];
  for (let index = 0; index < localDeleteThreadMsgs.length; index++) {
    const threadMessage = localDeleteThreadMsgs[index];
    const isNotDisappear = threadMessage.disappear_time > Date.now() || false === Boolean(threadMessage.disappear_time);
    const countDeleteOnlyMe = await getInteractor(prefixKey).LocalMessageService.count({
      parent_id: threadMessage.source_id,
      send_type: SystemConstant.SEND_TYPE.deleteMessageOnlyMe,
    });
    if (isNotDisappear && countDeleteOnlyMe === 0) {
      validRestoreThreadMessagesId.push(threadMessage.id);
    }
  }
  if (validRestoreThreadMessagesId.length > 0)
    updateMessageListId = updateMessageListId.concat(validRestoreThreadMessagesId);

  /**
   * Check child messages
   * Restore child messages if parent message is deleted only me at the first time
   */
  const totalParentDeleteOnlyMe = await getInteractor(prefixKey).LocalMessageService.count({
    parent_id: restoreMessage.sourceId,
    send_type: SystemConstant.SEND_TYPE.deleteMessageOnlyMe,
  });
  if (totalParentDeleteOnlyMe === 1) {
    const localDeleteChildMsgs = await getInteractor(prefixKey).LocalMessageService.find({
      parent_id: restoreMessage.sourceId,
    });
    const validRestoreChildMessagesId = localDeleteChildMsgs
      .filter(item => item.disappear_time > Date.now() || false === Boolean(item.disappear_time))
      .map(item => item.id);

    if (Array.isArray(validRestoreChildMessagesId) && validRestoreChildMessagesId.length > 0)
      updateMessageListId = updateMessageListId.concat(validRestoreChildMessagesId);
  }

  await getInteractor(prefixKey).LocalMessageService.update(
    { state: SystemConstant.STATE.active },
    { id: updateMessageListId },
  );

  /**
   * Delete latest delete only me message (sendType = 27)
   */
  const lastDeleteOnlyMeMsg = await getInteractor(prefixKey).LocalMessageService.findOne(
    {
      send_type: SystemConstant.SEND_TYPE.deleteMessageOnlyMe,
      parent_id: restoreMessage.sourceId,
      state: SystemConstant.STATE.active,
    },
    `ORDER BY message.created DESC`,
  );

  if (lastDeleteOnlyMeMsg && lastDeleteOnlyMeMsg.id) {
    await getInteractor(prefixKey).LocalMessageService.delete(lastDeleteOnlyMeMsg.id);
  }

  // Update thread: total reply, total unread, state if thread existed
  const threadId = restoreMessage.threadId || restoreMessage.sourceId;
  const threadRecord = await getInteractor(prefixKey).LocalThreadService.findOne({
    thread_id: threadId,
  });
  if (threadRecord && threadRecord.id) {
    const totalReply = await getInteractor(prefixKey).LocalMessageService.countTotalReplyInThread(threadId);
    const totalUnread = await getInteractor(prefixKey).LocalMessageService.countUnreadMessageInThread(
      accountId,
      threadId,
    );
    const accountReply = await getInteractor(prefixKey).LocalMessageService.getAccountReply(threadId);

    await getInteractor(prefixKey).LocalThreadService.update(
      {
        state: totalReply > 0 ? SystemConstant.STATE.active : SystemConstant.STATE.inactive,
        total_reply: totalReply,
        total_unread: totalUnread,
        account_reply: JSON.stringify(accountReply || []),
      },
      { thread_id: threadId },
    );
  }

  // Update group last message if need
  const groupRecord = await getInteractor(prefixKey).LocalGroupService.findById(restoreMessage.groupId);
  const groupLastMessage = await getInteractor(prefixKey).LocalMessageService.getLastMessageByGroup(
    restoreMessage.groupId,
  );
  if (groupRecord && groupLastMessage && groupRecord.last_message_id !== groupLastMessage.id) {
    const updateGroup = await getInteractor(prefixKey).LocalGroupService.updateLastMessageOfGroup(
      groupRecord,
      groupLastMessage,
    );
    await getInteractor(prefixKey).LocalGroupService.update(
      {
        last_message: updateGroup.last_message,
        last_message_id: updateGroup.last_message_id,
      },
      { id: updateGroup.id },
    );
  }
};

export const updateParentDeleteMessageContent = async (prefixKey, newMessage) => {
  newMessage = toCamel(newMessage);

  if (newMessage.sendType !== SystemConstant.SEND_TYPE.deleteMessage) return false;

  await getInteractor(prefixKey).LocalMessageService.update({ content: "" }, { source_id: newMessage.parentId });

  return true;
};

/**
 * Delete calling-related messages after deleting calling history.
 *
 * @async
 * @function deleteMessageAfterDeleteCallingHistory
 * @param {Object} params - Parameters object
 * @param {string} params.prefixKey - Key used to scope services and data operations.
 * @param {string[] | undefined} params.room_ids - List of room IDs to target (optional).
 * @param {string[] | undefined} params.call_history_ids - List of call history record IDs (optional).
 * @returns {Promise<boolean>} - Returns `true` if deletion and updates succeed, `false` otherwise.
 *
 */
export const deleteMessageAfterDeleteCallingHistory = async ({ prefixKey, room_ids, call_history_ids }) => {
  try {
    const callingRecords = await getInteractor(prefixKey).LocalCallHistoryService.find({
      room_id: room_ids,
      id: call_history_ids,
    });

    if (callingRecords.length === 0) return false;

    const sourceIds = removeDuplicateInArray(callingRecords.map(item => item.source_id));

    // Update state = inactive for calling message
    await getInteractor(prefixKey).LocalMessageService.update(
      { state: SystemConstant.STATE.inactive, modified: Date.now() },
      { source_id: sourceIds },
    );

    // Create new message with sendType = deleteMessageOnlyMe to hide calling message in UI
    const makingMessageSourceIds = [];
    await Promise.all(
      callingRecords.map(async callingRecordItem => {
        if (makingMessageSourceIds.includes(callingRecordItem.source_id)) return;
        makingMessageSourceIds.push(callingRecordItem.source_id);

        await saveMessageInQueue({
          prefixKey,
          groupId: callingRecordItem.group_id,
          content: JSON.stringify({ source_id: callingRecordItem.source_id }),
          parentId: callingRecordItem.source_id,
          sendType: SystemConstant.SEND_TYPE.deleteMessageOnlyMe,
          options: {},
        });
      }),
    );

    return true;
  } catch (error) {
    console.error("delete message after delete calling history error: ", error);
  }

  return false;
};
